import type { Insertable, Kysely, Selectable } from 'kysely';
import { getDesktopDb as getDesktopDatabase } from './desktop';
import './schema.desktop-augment';
import type { DB } from './schema';

const PRIMARY_LABEL = 'primary';

export interface EncryptedDesktopCookiePayload {
  capturedAt?: string;
  ciphertext: string;
  expiresAt?: string | null;
  nonce: string;
}

type DesktopAuthCookiesRow = Selectable<DB['desktopAuthCookies']>;

function resolveDatabase(dbh?: Kysely<DB>): Kysely<DB> | Promise<Kysely<DB>> {
  if (dbh !== undefined) {
    return dbh;
  }
  return getDesktopDatabase();
}

export async function saveEncryptedDesktopCookies(
  payload: EncryptedDesktopCookiePayload,
  dbh?: Kysely<DB>
): Promise<void> {
  const database = await resolveDatabase(dbh);
  const capturedAt = payload.capturedAt ?? new Date().toISOString();
  const insertRow: Insertable<DB['desktopAuthCookies']> = {
    label: PRIMARY_LABEL,
    ciphertext: payload.ciphertext,
    nonce: payload.nonce,
    expiresAt: payload.expiresAt ?? null,
    createdAt: capturedAt,
    updatedAt: capturedAt,
  };
  await database
    .insertInto('desktopAuthCookies')
    .values(insertRow)
    .onConflict((oc) =>
      oc.column('label').doUpdateSet({
        ciphertext: payload.ciphertext,
        nonce: payload.nonce,
        expiresAt: payload.expiresAt ?? null,
        updatedAt: capturedAt,
      })
    )
    .execute();
}

export async function getEncryptedDesktopCookies(dbh?: Kysely<DB>): Promise<DesktopAuthCookiesRow | undefined> {
  const database = await resolveDatabase(dbh);
  const row = await database
    .selectFrom('desktopAuthCookies')
    .selectAll()
    .where('label', '=', PRIMARY_LABEL)
    .executeTakeFirst();
  return row ?? undefined;
}

export async function clearDesktopCookies(dbh?: Kysely<DB>): Promise<void> {
  const database = await resolveDatabase(dbh);
  await database.deleteFrom('desktopAuthCookies').where('label', '=', PRIMARY_LABEL).execute();
}
